/**
 * 表示条件トリガーの判定（BR-U6-3、US-D2-08）。
 * displayTrigger/displayParamsに従い、クライアント側でリアルタイムに監視する。
 * 表示条件を満たすまで、ポップアップUI自体をDOMに構築しない。
 *
 * NFR Design 1.2（Bulkhead per Tag Instance）準拠: モジュールグローバルな状態を持たず、
 * createDisplayConditionMonitor()でタグごとに独立したインスタンスを生成する（tagCache.tsと同パターン）。
 *
 * <p><b>2026-08-07 バグ修正（電子ブック閲覧の表示条件が機能しない）</b>:
 * <ul>
 *   <li>displayParamsはspica-api（ResolveScenarioResponse.displayParams）がDBの生JSON文字列を
 *       そのまま返す契約であり、オブジェクトではない。従来はオブジェクトとして参照していたため
 *       `seconds`が常にundefinedとなり、AFTER_SECONDSは0秒＝即時表示で実質機能していなかった。
 *       JSON文字列・オブジェクトの双方を受け付けて正規化する。</li>
 *   <li>サーバー側で表示判定が完結するトリガー（ALWAYS/DISABLED/BY_PREVIOUS_ANSWER）が
 *       switchのどのcaseにも該当せず、監視が何も開始されないままポップアップが永久に
 *       表示されない状態になっていた。これらは即時表示へフォールバックする。</li>
 * </ul>
 */

import { logClientWarning } from "../core/errorHandler";
import type { DisplayTrigger } from "../types";

type PopupDisplayCallback = () => void;

/** displayParamsの受け入れ形（spica-apiはJSON文字列、モック・テストはオブジェクトを渡す）。 */
export type DisplayParamsInput = Record<string, unknown> | string | null | undefined;

export interface DisplayConditionMonitor {
  /**
   * 表示条件監視を開始する。条件を満たした時点でonDisplayが呼ばれ、以降は監視を停止する。
   */
  start(
    displayTrigger: DisplayTrigger,
    displayParams: DisplayParamsInput,
    onDisplay: PopupDisplayCallback
  ): void;
  /** ページ推移イベント（ホストの電子ブックビューア由来）を監視ロジックへ渡す。 */
  notifyPageTransition(currentPage: number, totalPages: number): void;
  /** 監視を停止する（ページ離脱時・条件成立後に呼ばれる）。 */
  stop(): void;
}

/**
 * displayParamsをオブジェクトへ正規化する。
 * spica-apiはDBの生JSON文字列をそのまま返すため、文字列で来た場合はJSON.parseする。
 */
function normalizeDisplayParams(displayParams: DisplayParamsInput): Record<string, unknown> {
  if (!displayParams) {
    return {};
  }
  if (typeof displayParams !== "string") {
    return displayParams;
  }
  try {
    const parsed: unknown = JSON.parse(displayParams);
    return parsed !== null && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
  } catch {
    logClientWarning("displayParams is not valid JSON");
    return {};
  }
}

/** 有限かつ正の数値のみを返す（それ以外はundefined）。 */
function toPositiveNumber(value: unknown): number | undefined {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : undefined;
}

export function createDisplayConditionMonitor(): DisplayConditionMonitor {
  let timerId: ReturnType<typeof setTimeout> | undefined;
  let onDisplayCallback: PopupDisplayCallback | null = null;
  let stopped = false;

  function handleVisibilityChange(): void {
    if (document.visibilityState === "hidden") {
      stop();
    }
  }

  function triggerPopupDisplay(): void {
    if (stopped) return;
    onDisplayCallback?.();
    stop();
  }

  function start(
    displayTrigger: DisplayTrigger,
    displayParams: DisplayParamsInput,
    onDisplay: PopupDisplayCallback
  ): void {
    onDisplayCallback = onDisplay;
    stopped = false;

    const params = normalizeDisplayParams(displayParams);

    // ページ離脱時に確実に監視を停止する（BR-U6-3）。
    // switchより前に登録することで、即時表示（stop()が同期的に走るケース）でも確実に解除される。
    window.addEventListener("beforeunload", stop, { once: true });
    document.addEventListener("visibilitychange", handleVisibilityChange);

    switch (displayTrigger) {
      case "AFTER_SECONDS": {
        const seconds = toPositiveNumber(params.seconds);
        if (seconds === undefined) {
          // 管理画面では秒数が必須。欠落・不正値は設定不整合のため即時表示へフォールバックする。
          logClientWarning("AFTER_SECONDS: params.seconds is missing or invalid");
          triggerPopupDisplay();
          break;
        }
        timerId = setTimeout(() => triggerPopupDisplay(), seconds * 1000);
        break;
      }
      default: {
        // ALWAYS / DISABLED / BY_PREVIOUS_ANSWER 等はサーバー側で表示判定が完結しているため、
        // ここまで到達した時点で表示してよい（クライアント側の追加条件なし）。
        triggerPopupDisplay();
        break;
      }
    }
  }

  /** ページ推移通知（到達率廃止により現在はno-op。外部APIとしてインターフェースのみ維持）。 */
  function notifyPageTransition(_currentPage: number, _totalPages: number): void {
    // no-op: PAGE_RATIO廃止に伴いクライアント側のページ到達判定は不要になった
  }

  function stop(): void {
    stopped = true;
    if (timerId) {
      clearTimeout(timerId);
      timerId = undefined;
    }
    document.removeEventListener("visibilitychange", handleVisibilityChange);
  }

  return { start, notifyPageTransition, stop };
}
